import { Link } from "react-router-dom";

import { usePendingPresentations } from "@/api/presentation-review";
import { usePendingReview } from "@/api/review";
import { EmptyState } from "@/components/EmptyState";
import { ErrorBanner } from "@/components/ErrorBanner";
import { ReviewPanel } from "@/components/review/ReviewPanel";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { PendingPresentationItem } from "@/api/types";
import { formatJobNumber } from "@/lib/format";

/**
 * `/review` — the cross-job HITL queue. One stacked `<ReviewPanel>` per
 * `pending_review` job, in `created_at DESC` order. Each panel submits
 * independently (per-job is the orchestrator's commit boundary), so a
 * 502 `catalogue_promote_failed` on one batch never affects the others.
 *
 * The queue refreshes after any submit because `useSubmitReview`
 * invalidates `["review-pending"]`. A finalised batch (rollup ran)
 * disappears from the list on the next refetch; partially-decided
 * batches keep their locked-row overlay.
 */
export function ReviewQueue() {
  const draftQuery = usePendingReview();
  const presentationQuery = usePendingPresentations();

  if (draftQuery.isPending || presentationQuery.isPending) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  if (draftQuery.isError) {
    return (
      <ErrorBanner error={draftQuery.error} onRetry={() => void draftQuery.refetch()} />
    );
  }
  if (presentationQuery.isError) {
    return (
      <ErrorBanner
        error={presentationQuery.error}
        onRetry={() => void presentationQuery.refetch()}
      />
    );
  }

  const batches = draftQuery.data.batches;
  const presentations = presentationQuery.data.presentations;

  const totalItems = batches.reduce((acc, b) => acc + b.items.length, 0);
  const totalRemaining = batches.reduce(
    (acc, b) => acc + b.items.filter((i) => i.existingDecision === null).length,
    0,
  );

  if (batches.length === 0 && presentations.length === 0) {
    return (
      <div className="space-y-6">
        <Header
          batches={0}
          remaining={0}
          total={0}
          pendingPresentations={0}
        />
        <EmptyState
          title="All caught up"
          description="No batches or presentations awaiting review."
        />
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <Header
        batches={batches.length}
        remaining={totalRemaining}
        total={totalItems}
        pendingPresentations={presentations.length}
      />

      {presentations.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold">Pending presentations</h2>
          <div className="space-y-3">
            {presentations.map((p) => (
              <PendingPresentationCard key={p.id} presentation={p} />
            ))}
          </div>
        </section>
      )}

      {batches.length > 0 && (
        <section className="space-y-6">
          {presentations.length > 0 && (
            <h2 className="text-lg font-semibold">Pending curator batches</h2>
          )}
          {batches.map((batch) => (
            <ReviewPanel batch={batch} key={batch.jobId} />
          ))}
        </section>
      )}
    </div>
  );
}

function PendingPresentationCard({
  presentation,
}: {
  presentation: PendingPresentationItem;
}) {
  const deadlineText = presentation.reviewDeadlineAt
    ? new Date(presentation.reviewDeadlineAt).toLocaleString()
    : null;
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>
              <Link
                to={`/presentations/${formatJobNumber(presentation.memoNumber)}/review`}
                className="underline-offset-2 hover:underline"
              >
                Presentation {presentation.shortId}
              </Link>
            </CardTitle>
            <CardDescription>
              Memo {presentation.memoShortId}
              {presentation.solutionType && <> · {presentation.solutionType}</>}
              {deadlineText && <> · Deadline {deadlineText}</>}
            </CardDescription>
          </div>
          <div className="flex flex-col items-end gap-1">
            <Badge
              variant={
                presentation.matchValidationStatus === "flagged"
                  ? "danger"
                  : "default"
              }
            >
              {presentation.matchValidationStatus}
            </Badge>
            {presentation.matchValidationScore !== null && (
              <span className="text-xs text-muted-foreground">
                Score {Math.round(presentation.matchValidationScore * 100)}%
              </span>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground">
        {presentation.recommendationCount}{" "}
        {presentation.recommendationCount === 1 ? "recommendation" : "recommendations"}
        {presentation.removedCount > 0 && (
          <> · {presentation.removedCount} removed</>
        )}
      </CardContent>
    </Card>
  );
}

function Header({
  batches,
  remaining,
  total,
  pendingPresentations,
}: {
  batches: number;
  remaining: number;
  total: number;
  pendingPresentations: number;
}) {
  const parts: string[] = [];
  if (pendingPresentations > 0) {
    parts.push(
      `${pendingPresentations} presentation${pendingPresentations === 1 ? "" : "s"}`,
    );
  }
  if (batches > 0) {
    parts.push(
      `${batches} batch${batches === 1 ? "" : "es"} · ${remaining} of ${total} item${total === 1 ? "" : "s"} remaining`,
    );
  }
  return (
    <div>
      <p className="label-mono">Pending review</p>
      <h1 className="font-display text-4xl text-text">Review queue</h1>
      <p className="mt-2 text-sm text-text-muted">
        {parts.length === 0 ? "Nothing awaiting decision." : parts.join(" · ")}
      </p>
    </div>
  );
}
